import type { ActionSummary, ContextSummary, ProjectSummary } from '../../../core/api/types';
import { countPhrase } from '../../../shared/actions/count-phrase';

export interface SidebarEntry {
  readonly id: number;
  readonly name: string;
  readonly phrase: string;
  /** Nothing available and nothing waiting — the only genuinely empty case. */
  readonly isEmpty: boolean;
}

/**
 * 009 FR-010: what the sidebar lists. Both fields are **required** — an optional pair defaulted here
 * would be a second statement of the defaults on the client (FR-015) and would let a caller keep
 * today's behaviour without saying so. One object rather than two positional booleans, because two
 * adjacent booleans in a six-argument call are a swap waiting to happen.
 */
export interface SidebarVisibility {
  /** A completed project is `state === 'completed'` (research R-007). Defaults off — see FR-003. */
  readonly showCompletedProjects: boolean;
  /** A hidden context is `isActive === false` (research R-007). Defaults on — see FR-003. */
  readonly showHiddenContexts: boolean;
}

export interface Sidebar {
  readonly contexts: readonly SidebarEntry[];
  readonly projects: readonly SidebarEntry[];
}

// `countPhrase` moved to `shared/actions/count-phrase.ts` (research R-008; T022) — 007's container
// rows need the same rule, from the same place.

export function buildSidebar(
  contexts: readonly ContextSummary[],
  projects: readonly ProjectSummary[],
  available: readonly ActionSummary[],
  deferred: readonly ActionSummary[],
  visibility: SidebarVisibility,
): Sidebar {
  const tally = (actions: readonly ActionSummary[], key: (a: ActionSummary) => number | null) => {
    const counts = new Map<number, number>();
    for (const action of actions) {
      const id = key(action);
      if (id !== null) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  };

  const availableByContext = tally(available, (a) => a.contextId);
  const deferredByContext = tally(deferred, (a) => a.contextId);
  const availableByProject = tally(available, (a) => a.projectId ?? null);
  const deferredByProject = tally(deferred, (a) => a.projectId ?? null);

  const entry = (
    id: number, name: string,
    availableCounts: Map<number, number>, deferredCounts: Map<number, number>,
  ): SidebarEntry => {
    const open = availableCounts.get(id) ?? 0;
    const waiting = deferredCounts.get(id) ?? 0;
    return { id, name, phrase: countPhrase(open, waiting), isEmpty: open === 0 && waiting === 0 };
  };

  // Both switches decide what is *listed*, never what is counted: the tallies above run over every
  // action the page loaded, so a surviving entry's phrase is the same whichever way they are set
  // (SC-006). A project that is merely hidden stays listed, as it does today — two switches, not three.
  return {
    contexts: contexts
      .filter((c) => visibility.showHiddenContexts || c.isActive)
      .map((c) => entry(c.id, c.name, availableByContext, deferredByContext)),
    projects: projects
      .filter((p) => visibility.showCompletedProjects || p.state !== 'completed')
      .map((p) => entry(p.id, p.name, availableByProject, deferredByProject)),
  };
}
